// ============================================================================
// MÓDULO: RDA UI — BIBLIOTECA DE COMPONENTES BASE (V1.0)
// ARQUIVO: src/components/ui/RdaUI.jsx
// OBJETIVO: Componentes de UI padronizados para o Portal Jurídico RDA.
//           Espelho React do rda_gui_style.py (ferramentas desktop).
//
// CORES INSTITUCIONAIS (CONSTITUIÇÃO V75.0 — INVIOLÁVEIS):
//   --brand-blue:     #20243B  (Azul Institucional)
//   --highlight-gold: #C5A059  (Champagne Gold)
//   --highlight-red:  #dc2626  (Vermelho — alertas)
//
// EXPORTS:
//   ModuleHeader  — cabeçalho azul + linha dourada (padrão de todos os módulos)
//   RdaButton     — botão CTA com hover dourado (primary / outline / gold / danger)
//   RdaCard       — card com borda e sombra padrão
//   SectionLabel  — título de seção interno
//   StatusBadge   — badge de status com cores semânticas
// ============================================================================

import React, { useState } from 'react';

// ===========================================================================
// CONSTANTES DE COR
// ===========================================================================
export const RDA_BLUE = '#20243B';
export const RDA_GOLD = '#C5A059';
export const RDA_RED  = '#dc2626';

// ===========================================================================
// ModuleHeader
// ---------------------------------------------------------------------------
// Cabeçalho padronizado para cada módulo do portal.
// Equivalente à função _build_header() do rda_gui_style.py.
//
// Props:
//   title    {string}    — título principal (branco sobre azul)
//   subtitle {string?}   — subtítulo em dourado (ex: "Gestão de Autos")
//   actions  {ReactNode} — botões de ação alinhados à direita (opcional)
//
// Exemplo:
//   <ModuleHeader
//     title="Processos"
//     subtitle="Gestão de Autos"
//     actions={<RdaButton icon="fa-plus" onClick={handleNovo}>Novo</RdaButton>}
//   />
// ===========================================================================
export function ModuleHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col mb-6 mx-4 mt-4 rounded-lg overflow-hidden shadow-sm">
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ background: RDA_BLUE }}
      >
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-white font-bold text-sm tracking-wide truncate">
            {title}
          </span>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {actions}
          </div>
        )}
      </div>
      <div className="h-0.5" style={{ background: RDA_GOLD }} />
    </div>
  );
}

// ===========================================================================
// RdaButton
// ---------------------------------------------------------------------------
// Botão CTA padrão RDA com hover dourado.
// Equivalente ao make_cta_btn() do rda_gui_style.py.
//
// Props:
//   variant  — 'primary' (azul→ouro) | 'outline' | 'gold' | 'danger'
//   size     — 'sm' | 'md' | 'lg' (lg ocupa largura total)
//   icon     — classe FontAwesome (ex: 'fa-plus', 'fa-save')
//   disabled — boolean
// ===========================================================================
export function RdaButton({
  children,
  onClick,
  variant = 'primary',
  size    = 'md',
  icon,
  disabled = false,
  type     = 'button',
  className = '',
}) {
  const [hovered, setHovered] = useState(false);

  const sizeClass = {
    sm: 'px-3 py-1.5 text-[11px]',
    md: 'px-4 py-2 text-xs',
    lg: 'w-full py-3 text-sm',
  }[size] ?? 'px-4 py-2 text-xs';

  const getStyle = () => {
    if (disabled) return { background: '#9CA3AF', color: '#fff', border: 'none' };
    switch (variant) {
      case 'primary':
        return { background: hovered ? RDA_GOLD : RDA_BLUE, color: '#fff', border: 'none' };
      case 'gold':
        return { background: hovered ? RDA_BLUE : RDA_GOLD, color: hovered ? '#fff' : RDA_BLUE, border: 'none' };
      case 'outline':
        return { background: hovered ? RDA_BLUE : 'transparent', color: hovered ? '#fff' : RDA_BLUE, border: `1.5px solid ${RDA_BLUE}` };
      case 'danger':
        return { background: hovered ? '#991b1b' : RDA_RED, color: '#fff', border: 'none' };
      default:
        return { background: RDA_BLUE, color: '#fff', border: 'none' };
    }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 font-semibold rounded transition-all cursor-pointer disabled:cursor-not-allowed ${sizeClass} ${className}`}
      style={getStyle()}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {icon && <i className={`fas ${icon} text-[10px]`} aria-hidden="true" />}
      {children}
    </button>
  );
}

// ===========================================================================
// RdaCard
// ---------------------------------------------------------------------------
// Card padronizado com borda sutil e sombra RDA.
//
// Props:
//   noPadding {bool}   — remove o padding interno (para uso com header próprio)
//   className {string} — classes Tailwind adicionais
// ===========================================================================
export function RdaCard({ children, className = '', noPadding = false }) {
  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 shadow-sm ${noPadding ? '' : 'p-5'} ${className}`}
    >
      {children}
    </div>
  );
}

// ===========================================================================
// SectionLabel
// ---------------------------------------------------------------------------
// Título de seção interna — linha cinza com texto em maiúsculas.
//
// Exemplo:
//   <SectionLabel>Informações do Processo</SectionLabel>
// ===========================================================================
export function SectionLabel({ children, className = '' }) {
  return (
    <div className={`flex items-center gap-3 mb-3 ${className}`}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 whitespace-nowrap">
        {children}
      </span>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// ===========================================================================
// StatusBadge
// ---------------------------------------------------------------------------
// Badge de status com cores semânticas RDA.
//
// status: 'active' | 'inactive' | 'warning' | 'danger' | 'info' | 'gold'
// ===========================================================================
export function StatusBadge({ status = 'inactive', label }) {
  const map = {
    active:   { bg: '#dcfce7', color: '#166534' },
    inactive: { bg: '#f3f4f6', color: '#6b7280' },
    warning:  { bg: '#fef3c7', color: '#92400e' },
    danger:   { bg: '#fee2e2', color: '#991b1b' },
    info:     { bg: '#dbeafe', color: '#1e40af' },
    gold:     { bg: '#fef9ed', color: '#92400e', border: `1px solid ${RDA_GOLD}33` },
  };
  const s = map[status] ?? map.inactive;

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color, border: s.border }}
    >
      {label}
    </span>
  );
}

// ===========================================================================
// EmptyState
// ---------------------------------------------------------------------------
// Estado vazio padronizado para listas/tabelas sem dados.
//
// Props:
//   icon    — classe FontAwesome (ex: 'fa-folder-open')
//   title   — mensagem principal
//   message — detalhe opcional
// ===========================================================================
export function EmptyState({ icon = 'fa-inbox', title = 'Nenhum item encontrado', message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <i className={`fas ${icon} text-3xl mb-3 opacity-20 text-gray-400`} aria-hidden="true" />
      <p className="text-sm font-bold text-gray-400">{title}</p>
      {message && <p className="text-xs text-gray-400 mt-1">{message}</p>}
    </div>
  );
}
